import React, { useEffect, useState } from 'react';
import { Loader2, X, CheckCircle2, XCircle, AlertTriangle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ kind, children }) => {
  const cls = {
    ok: 'bg-green-50 text-green-700 border-green-200',
    warn: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    err: 'bg-red-50 text-red-700 border-red-200',
    mute: 'bg-gray-100 text-gray-700 border-gray-200',
  }[kind] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {children}
    </span>
  );
};

const SLOT_LABEL = {
  whatsapp_generated: 'WhatsApp',
  radio_generated: 'Radio',
  social_generated: 'Social',
  video_generated: 'Video',
  policy_generated: 'Policy',
  evidence_generated: 'Evidence',
};

export const AuditModal = ({ open, onClose, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bucket, setBucket] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/content-engine/audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;

  const rows = data?.rows || [];
  const filtered = rows.filter((r) => {
    if (bucket === 'all') return true;
    if (bucket === 'approved_transformed') return ['approved', 'published', 'used'].includes(r.status) && r.is_complete && r.version_current;
    if (bucket === 'approved_not_transformed') return ['approved', 'published', 'used'].includes(r.status) && (!r.is_complete || !r.version_current);
    if (bucket === 'pending') return ['incoming', 'under_review', 'submitted'].includes(r.status);
    if (bucket === 'rejected') return r.status === 'rejected';
    return true;
  });

  const summary = data?.summary;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="audit-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl font-bold text-gray-900">Engine Audit</h3>
            <p className="text-xs text-gray-500">Every story · transformation completeness · per-slot status</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="close-audit">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs">
            <Bucket label="Total" value={summary.total} onClick={() => setBucket('all')} active={bucket === 'all'} />
            <Bucket label="Approved + Transformed" value={summary.approved_transformed} kind="ok"
                    onClick={() => setBucket('approved_transformed')} active={bucket === 'approved_transformed'} />
            <Bucket label="Approved + NOT Transformed" value={summary.approved_not_transformed} kind="warn"
                    onClick={() => setBucket('approved_not_transformed')} active={bucket === 'approved_not_transformed'} />
            <Bucket label="Pending Review" value={summary.pending_review}
                    onClick={() => setBucket('pending')} active={bucket === 'pending'} />
            <Bucket label="Rejected" value={summary.rejected} kind="err"
                    onClick={() => setBucket('rejected')} active={bucket === 'rejected'} />
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center gap-2 text-gray-500 py-8 px-5">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading audit…
            </div>
          )}
          {error && <p className="text-sm text-red-600 px-5 py-3">{error}</p>}

          {!loading && !error && (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-600">
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Country</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Outputs</th>
                  <th className="px-4 py-2">Missing</th>
                </tr>
              </thead>
              <tbody data-testid="audit-rows">
                {filtered.map((r) => {
                  const approved = ['approved', 'published', 'used'].includes(r.status);
                  let rowKind = 'mute';
                  let rowIcon = <Clock className="w-3 h-3" />;
                  if (r.status === 'rejected') {
                    rowKind = 'err';
                    rowIcon = <XCircle className="w-3 h-3" />;
                  } else if (approved && r.is_complete && r.version_current) {
                    rowKind = 'ok';
                    rowIcon = <CheckCircle2 className="w-3 h-3" />;
                  } else if (approved) {
                    rowKind = 'warn';
                    rowIcon = <AlertTriangle className="w-3 h-3" />;
                  }
                  return (
                    <tr key={r.id} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-medium text-gray-900">
                        {r.title}
                        {r.author_name && <span className="text-gray-500 font-normal"> · {r.author_name}</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{r.country_name || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${rowKind === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : rowKind === 'warn' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : rowKind === 'err' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {rowIcon} {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {r.outputs_generated}/6
                        {r.generation_status === 'running' && <span className="ml-1 text-yellow-600">· running</span>}
                        {r.generation_status === 'failed' && <span className="ml-1 text-red-600">· failed</span>}
                      </td>
                      <td className="px-4 py-2">
                        {r.missing && r.missing.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {r.missing.map((m) => (
                              <StatusPill key={m} kind="warn">{SLOT_LABEL[m] || m}</StatusPill>
                            ))}
                          </div>
                        ) : (
                          <StatusPill kind="ok">complete</StatusPill>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No stories in this bucket.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>{data?.generated_at && `Generated ${new Date(data.generated_at).toLocaleString()}`}</span>
          <button type="button" onClick={load} className="px-3 py-1 rounded border border-gray-300 hover:border-[#07743F] hover:text-[#07743F]">
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

const Bucket = ({ label, value, onClick, active, kind }) => {
  const tone = {
    ok: active ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700',
    warn: active ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-700',
    err: active ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700',
  }[kind] || (active ? 'bg-[#07743F] text-white' : 'bg-white text-gray-700 border border-gray-200');
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`audit-bucket-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex flex-col items-start px-3 py-2 rounded-lg transition ${tone}`}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-80">{label}</span>
      <span className="text-lg font-bold leading-none">{value}</span>
    </button>
  );
};

export default AuditModal;
